/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable react/no-did-mount-set-state */

import { getExtensionRoutes, UserPage } from '@cedalo/webui-extensions';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import fetch from 'isomorphic-fetch';
import React, { useEffect, useState } from 'react';
import { TourProvider } from '@reactour/tour';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import 'typeface-roboto';
import './App.css';
import LoginPage from './components/Auth/LoginPage';
import LogoutPage from './components/Auth/LogoutPage';
import { graphManager } from './GraphManager';
import ConfigManager from './helper/ConfigManager';
import { Path } from './helper/Path';
import theme from './theme';
import { AppProvider } from './ui/app/AppProvider';
import { AppPage } from './ui/pages/AppPage';
import { AppsPage } from './ui/pages/AppsPage';
import { ExportPage } from './ui/pages/ExportPage';
import { HomePage } from './ui/pages/HomePage';
import { LoadingPage } from './ui/pages/LoadingPage';
import SettingsPage from './ui/pages/SettingsPage';
import { EnsureUser, UserProvider, withUser } from './UserProvider';
import { toHashObject } from './ui/utils/URLHash';

const GATEWAY_CONFIG = ConfigManager.config.gatewayClientConfig;

const LazySetup = () => {
	const [Component, setComponent] = useState(null);
	useEffect(async () => {
		const c = await import('./components/Auth/SetupPage');
		setComponent(c.default);
	});
	return Component ? <Component /> : null;
};

const AppPageWrapper = withUser(() => ({}))((props) => {
	const noEdit = !!toHashObject(window.location.hash).preview;
	const { machineId } = useParams();
	return (
		<AppProvider {...props} machineId={machineId} noEdit={noEdit}>
			<AppPage {...props} />
		</AppProvider>
	);
});
const isLicenseAccepted = (setup) => setup.licenseAgreement && setup.licenseAgreement.accepted;
const isSetupCompleted = (setup) => setup && isLicenseAccepted(setup);
const App = () => {
	const [setupComplete, setSetupComplete] = useState(false);
	const [step, setStep] = useState(0);
	const [steps, setSteps] = useState();
	const navigate = useNavigate();

	useEffect(() => {
		const run = async () => {
			const response = await fetch(`${GATEWAY_CONFIG.restEndpointURL}/system/setup`);
			const setup = await response.json();
			const isComplete = isSetupCompleted(setup);
			setSetupComplete(isComplete);
			if (!isComplete) {
				navigate('/setup');
			}
		};
		run();
	}, []);

	const setCurrentStep = (stp, stps) => {
		if (stps) {
			setSteps(stps);
		} else {
			stps = steps;
		}

		if (stps.length > stp && stps[stp].routing) {
			navigate(stps[stp].routing, true);
		}
		if (stps.length > stp && stps[stp].hash !== undefined) {
			window.location.hash = stps[stp].hash;
		}

		if (stps[stp].range || stps[stp].shape) {
			graphManager.prepareTourStep(stps[stp]);
		} else if (graphManager.graph.tourHighlights) {
			graphManager.graph.tourHighlights = undefined;
			graphManager.redraw();
		}

		setStep(stp);
	};

	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={theme}>
				<UserProvider>
					<div
						style={{
							height: '100%',
							width: '100%'
						}}
					>
						<TourProvider
							// steps={steps}
							currentStep={step}
							setCurrentStep={(stp, stps) => setCurrentStep(stp, stps)}
							styles={{
								close: (base) => ({ ...base, right: 8, top: 8 })
							}}
							showDots={false}
							beforeClose={() => {
								graphManager.graph.tourHighlights = undefined;
								graphManager.redraw();
							}}
						>
							<Routes data-tour="app">
								{!setupComplete ? (
									<Route path="/setup" element={<LazySetup />} />
								) : (
									<React.Fragment>
										<Route exact path="/" element={<Navigate to="/home" replace />} />
										<Route path="/login" element={<LoginPage />} />
										<Route
											path="/home"
											element={
												<EnsureUser>
													<HomePage />
												</EnsureUser>
											}
										/>
										<Route
											path="/loading"
											element={
												<EnsureUser>
													<LoadingPage />
												</EnsureUser>
											}
										/>
										<Route
											exact
											path="/apps"
											element={
												<EnsureUser>
													<AppsPage />
												</EnsureUser>
											}
										/>
										<Route path="/logout" element={<LogoutPage />} />
										{process.env.REACT_APP_HIDE_ADMIN ? null : (
											<React.Fragment>
												<Route
													exact
													path="/administration"
													element={<Navigate to={Path.users()} replace />}
												/>
												{/* <Route path="/administration/plugins/" component={DefaultLayout} /> */}
												<Route
													path="/settings"
													element={
														<EnsureUser>
															<SettingsPage />
														</EnsureUser>
													}
												/>
											</React.Fragment>
										)}
										<Route path="users">
											<Route
												index
												element={
													<EnsureUser>
														<UserPage />
													</EnsureUser>
												}
											/>
											<Route
												path=":userId"
												element={
													<EnsureUser>
														<UserPage />
													</EnsureUser>
												}
											/>
										</Route>

										<Route
											path="/export"
											element={
												<EnsureUser>
													<ExportPage />
												</EnsureUser>
											}
										/>
										<Route
											path="/apps/:machineId"
											element={
												<EnsureUser>
													<AppPageWrapper />
												</EnsureUser>
											}
										/>
										<Route path="*" element={<Navigate to="/home" />} />
										{getExtensionRoutes()}
									</React.Fragment>
								)}
							</Routes>
						</TourProvider>
					</div>
				</UserProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

export default App;
